import { useState, useEffect } from "react";
import { FileText } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// Generic "Create Docs" dialog (POST /docs/push-table): pushes a table of
// values into a brand-new doc, or as a NEW sheet tab inside a doc the user
// can edit — never into an existing sheet. Same flow as Sales Orders'
// Create Docs, but column-agnostic so any list page can reuse it.
//
// columns: [{ label, type: "text"|"number"|"money", total?, width? }]
// rows:    [[cell, ...]] in column order
// Every column header has a checkbox — unticked columns are left out of the
// pushed sheet (and out of groupCols), so any module's push is pick-your-columns.
// groupCols: leading columns merged down runs of the same column-0 value
// onOpenDoc(href): open the result in-app (e.g. as an ERP tab) instead of a
//                  new browser tab
export default function PushToDocsDialog({ open, onOpenChange, token, title, defaultName, columns, rows, groupCols = 0, onPushed, onOpenDoc }) {
  const [mode, setMode] = useState("new"); // "new" | "existing"
  const [name, setName] = useState("");
  const [docId, setDocId] = useState("");
  const [docList, setDocList] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [offCols, setOffCols] = useState(() => new Set()); // unticked column indexes

  useEffect(() => {
    if (!open) return;
    setMode("new");
    setName(defaultName || "");
    setDocId("");
    setOffCols(new Set());
    setLoadingDocs(true);
    fetch(`${API}/docs/series`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        // A DOC here is one specific document; the push lands in it as a new sheet tab.
        setDocList((Array.isArray(data) ? data : [])
          .filter((s) => s.can_edit)
          .flatMap((s) => (Array.isArray(s.docs) ? s.docs : []).map((d) => ({
            id: d.id,
            label: `${s.name} — ${d.title || d.code || "Untitled"}`,
          })))
          .filter((d) => d.id));
      })
      .catch((e) => console.error("Error fetching docs:", e))
      .finally(() => setLoadingDocs(false));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const keep = columns.map((_, i) => i).filter((i) => !offCols.has(i));
  const toggleCol = (i) => setOffCols((prev) => {
    const next = new Set(prev);
    if (next.has(i)) next.delete(i); else next.add(i);
    return next;
  });

  const submit = async () => {
    if (!rows.length) return;
    if (!keep.length) { toast.error("Tick at least one column"); return; }
    if (!name.trim()) { toast.error("Sheet name is required"); return; }
    if (mode === "existing" && !docId) { toast.error("Choose a doc to add the sheet to"); return; }
    setSubmitting(true);
    try {
      const response = await fetch(`${API}/docs/push-table`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          mode, doc_id: mode === "existing" ? docId : undefined,
          name: name.trim(),
          columns: keep.map((i) => columns[i]),
          rows: rows.map((row) => keep.map((i) => row[i])),
          // Ticked group columns are still the leading ones, just fewer.
          group_cols: keep.filter((i) => i < groupCols).length,
        }),
      });
      const data = await response.json();
      if (!response.ok) { toast.error(data?.error || "Failed to push to Docs"); return; }
      toast.success(`Pushed ${rows.length} row${rows.length > 1 ? "s" : ""} to ${data.title || "Docs"}`);
      // A doc already open in a kept-alive tab still holds the workbook it
      // loaded — DocEditorPage reloads on this so it can't save that copy
      // back over the new sheet.
      if (data.mode === "existing" && data.doc_id) {
        window.dispatchEvent(new CustomEvent("erp:doc-pushed", { detail: { doc_id: data.doc_id } }));
      }
      onOpenChange(false);
      onPushed?.(data);
      if (data.series_id && data.doc_id) {
        const href = `/docs/${data.series_id}/${data.doc_id}`;
        if (onOpenDoc) onOpenDoc(href);
        else window.open(href, "_blank");
      }
    } catch (e) {
      console.error("Error pushing to Docs:", e);
      toast.error("Failed to push to Docs");
    } finally {
      setSubmitting(false);
    }
  };

  const fmt = (v, type) => (v === "" || v === null || v === undefined ? "-"
    : type === "money" ? Number(v).toFixed(2) : String(v));
  const modeBtn = (value, label) => (
    <Button
      type="button"
      variant={mode === value ? "default" : "outline"}
      className={mode === value ? "bg-indigo-600 hover:bg-indigo-700 text-white flex-1" : "flex-1"}
      onClick={() => setMode(value)}
    >
      {label}
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!submitting) onOpenChange(o); }}>
      {/* min-w-0 on the content wrapper lets the preview table scroll inside
          the dialog instead of forcing the grid item wider (see SalesPage). */}
      <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto overflow-x-hidden" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-600" />
            {title} ({rows.length} rows)
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2 min-w-0">
          <div className="flex gap-2">
            {modeBtn("new", "New Doc")}
            {modeBtn("existing", "New Sheet")}
          </div>

          {mode === "existing" && (
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider">Choose Doc *</Label>
              <select
                value={docId}
                onChange={(e) => setDocId(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-md border border-input bg-background"
                disabled={loadingDocs}
              >
                <option value="">{loadingDocs ? "Loading…" : "Select a doc"}</option>
                {docList.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
              </select>
              {!loadingDocs && docList.length === 0 && (
                <div className="text-xs text-muted-foreground">No editable docs found — use "New Doc" instead.</div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider">{mode === "new" ? "Sheet Name *" : "New Sheet Name *"}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={defaultName} />
          </div>

          <div className="border rounded-md overflow-x-auto max-h-[40vh]">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-muted/40">
                  {columns.map((c, ci) => (
                    <th key={ci} className={`px-2 py-1.5 font-bold whitespace-nowrap ${c.type === "text" ? "text-left" : "text-right"} ${offCols.has(ci) ? "text-muted-foreground font-normal" : ""}`}>
                      <label className="inline-flex items-center gap-1 cursor-pointer select-none" title={offCols.has(ci) ? "Not in doc — tick to include" : "In doc — untick to leave out"}>
                        <input
                          type="checkbox"
                          checked={!offCols.has(ci)}
                          onChange={() => toggleCol(ci)}
                          className="w-3 h-3 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                        />
                        {c.label}
                      </label>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} className="border-t">
                    {columns.map((c, ci) => (
                      <td key={ci} className={`px-2 py-1.5 ${c.type === "text" ? "truncate max-w-[160px]" : "text-right whitespace-nowrap"} ${offCols.has(ci) ? "text-muted-foreground/50 line-through" : ""}`}>{fmt(row[ci], c.type)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={submit} disabled={submitting || rows.length === 0 || keep.length === 0}>
              {submitting ? "Pushing…" : "Push to Docs"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
